import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useFailedSyncCount } from '@/hooks/useFailedSyncCount';
import { WifiOff, RefreshCcw, Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getFailedEffects, dominantFailedCategory } from '@/lib/syncQueue';
import { eventBus, SettingsEvents } from '@/lib/eventBus';

export function NetworkStatusBanner() {
  const { t } = useTranslation();
  const { isOnline, pendingCount } = useNetworkStatus();
  const failedCount = useFailedSyncCount();
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSynced, setShowSynced] = useState(false);

  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      setIsSyncing(true);
    } else if (isOnline && pendingCount === 0 && isSyncing) {
      setIsSyncing(false);
      setShowSynced(true);
      const timer = setTimeout(() => setShowSynced(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingCount, isSyncing]);

  // Safety: auto-dismiss syncing state after 15 seconds to prevent stuck banner
  useEffect(() => {
    if (!isSyncing) return;
    const safety = setTimeout(() => {
      setIsSyncing(false);
    }, 15000);
    return () => clearTimeout(safety);
  }, [isSyncing]);

  // Parked failures are the most important state: a mutation exhausted its
  // retry budget and silently sits in the dead-letter queue. Without this the
  // banner just stops spinning and the user assumes success — the exact
  // silent-data-loss gap we're closing. It outranks offline/syncing/synced
  // and stays put (it's actionable) until the user resolves it in Settings.
  if (failedCount > 0) {
    const category = dominantFailedCategory(getFailedEffects());
    const label = category
      ? t('networkBanner.failedWithCategory', {
          count: failedCount,
          category: t(`settings.failedSync.category.${category}`),
        })
      : t('networkBanner.failed', { count: failedCount });

    return (
      <button
        type="button"
        onClick={() => eventBus.emit(SettingsEvents.OPEN, { tab: 'failed-sync' })}
        aria-label={t('networkBanner.failedAction')}
        className={cn(
          'fixed bottom-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg',
          'bg-destructive text-destructive-foreground transition-all duration-500',
          'animate-in fade-in slide-in-from-bottom-4 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive/60',
        )}
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs font-semibold underline underline-offset-2">
          {t('networkBanner.failedAction')}
        </span>
      </button>
    );
  }

  if (isOnline && !isSyncing && !showSynced) return null;

  return (
    <div
      className={cn(
        "fixed bottom-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all duration-500 animate-in fade-in slide-in-from-bottom-4",
        !isOnline ? "bg-destructive text-destructive-foreground" :
        isSyncing ? "bg-primary text-primary-foreground" :
        "bg-green-700 text-white"
      )}
    >
      {!isOnline ? (
        <>
          <WifiOff className="w-4 h-4" />
          <span className="text-sm font-medium">
            {t('networkBanner.offline', { count: pendingCount })}
          </span>
        </>
      ) : isSyncing ? (
        <>
          <RefreshCcw className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">{t('networkBanner.syncing')}</span>
        </>
      ) : (
        <>
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">{t('networkBanner.synced')}</span>
        </>
      )}
    </div>
  );
}
